import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createAdminProduct,
  deleteAdminProduct,
  exportAdminProductsPdf,
  exportAdminProductsXlsx,
  fetchAdminProduct,
  fetchAdminProducts,
  setAdminProductActive,
  updateAdminProduct,
} from "@/lib/adminProductsApi";
import {
  createCatalogUnit,
  fetchCatalogMetadata,
  fetchCatalogUnits,
  fetchCatalogUnitUsage,
  updateCatalogUnit,
  type CatalogUnitInput,
} from "@/lib/catalogApi";
import { useApp } from "@/store/useApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, FileSpreadsheet, FileText, Loader2, Package, Pencil, Plus, Power, PowerOff, Scissors, Search, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ProductDialog, ProductThumb, emptyDraft, type DraftProduct } from "@/components/admin/ProductDialog";
import { LabelDialog } from "@/components/admin/LabelDialog";
import { hasPermission, type AdminProductSummary, type CatalogCategory, type CatalogCutType, type CatalogUnit, type ClientType, type Product } from "@/lib/types";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";

const formatPercent = (value: number) => (Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, ""));

export default function AdminProducts() {
  // Botões por PERMISSÃO individual (o backend revalida cada ação). Visualizar sem estas = só leitura.
  const account = useApp((state) => state.account);
  const canCreate = hasPermission(account, "CATALOGO.PRODUTO.CRIAR");
  const canEdit = hasPermission(account, "CATALOGO.PRODUTO.EDITAR");
  const canDelete = hasPermission(account, "CATALOGO.PRODUTO.EXCLUIR");
  const canActivate = hasPermission(account, "CATALOGO.PRODUTO.ATIVAR");
  const canDeactivate = hasPermission(account, "CATALOGO.PRODUTO.DESATIVAR");
  const canExport = hasPermission(account, "CATALOGO.PRODUTO.EXPORTAR");
  const canManageUnits = hasPermission(account, "CATALOGO.UNIDADE.CRIAR") || hasPermission(account, "CATALOGO.UNIDADE.EDITAR");
  const canGenerateLabels = hasPermission(account, "ETIQUETAS.ETIQUETA.GERAR");
  const canViewCost = hasPermission(account, "CATALOGO.CUSTO.VISUALIZAR");
  const canEditCost = hasPermission(account, "CATALOGO.CUSTO.EDITAR");
  const canManage = canCreate || canEdit;
  const [products, setProducts] = useState<AdminProductSummary[]>([]);
  const [catalogCategories, setCatalogCategories] = useState<CatalogCategory[]>([]);
  const [catalogUnits, setCatalogUnits] = useState<CatalogUnit[]>([]);
  const [catalogCutTypes, setCatalogCutTypes] = useState<CatalogCutType[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("ALL");
  const [segmentFilter, setSegmentFilter] = useState<ClientType | "ALL">("ALL");
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [editing, setEditing] = useState<DraftProduct | null>(null);
  const [labelProduct, setLabelProduct] = useState<Product | null>(null);
  const [labelOpen, setLabelOpen] = useState(false);
  const [exporting, setExporting] = useState<null | "pdf" | "xlsx">(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminProductSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadPageData = useCallback(async () => {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [productSummaries, metadata] = await Promise.all([fetchAdminProducts(), fetchCatalogMetadata()]);
      setProducts(productSummaries);
      setCatalogCategories(metadata.categories);
      setCatalogUnits(metadata.units);
      setCatalogCutTypes(metadata.cutTypes);
      setLoadState("ready");
    } catch (error) {
      setProducts([]);
      setCatalogCategories([]);
      setCatalogUnits([]);
      setCatalogCutTypes([]);
      setLoadState("error");
      setLoadError(error instanceof Error ? error.message : "Nao foi possivel carregar os produtos");
    }
  }, []);

  useEffect(() => {
    void loadPageData();
  }, [loadPageData]);

  const categoryOptions = useMemo(
    () => [{ id: "ALL", label: "Todas", emoji: "🌱" }, ...catalogCategories.map((category) => ({
      id: category.id,
      label: category.label,
      emoji: category.emoji,
    }))],
    [catalogCategories],
  );
  const categoryById = useMemo(
    () => new Map(catalogCategories.map((category) => [category.id, category] as const)),
    [catalogCategories],
  );
  // Recarrega só as unidades após criar/editar uma — atualiza a grade do ProductDialog sem reload da página.
  const refreshUnits = useCallback(async () => {
    setCatalogUnits(await fetchCatalogUnits());
  }, []);

  const handleCreateUnit = useCallback(
    async (input: CatalogUnitInput) => {
      const created = await createCatalogUnit(input);
      await refreshUnits();
      return created;
    },
    [refreshUnits],
  );

  const handleUpdateUnit = useCallback(
    async (id: string, input: CatalogUnitInput) => {
      const updated = await updateCatalogUnit(id, input);
      await refreshUnits();
      return updated;
    },
    [refreshUnits],
  );

  const cutOptions = useMemo(
    () => Array.from(new Set(catalogCutTypes.map((cutType) => cutType.name))),
    [catalogCutTypes],
  );

  const filtered = useMemo(() => {
    return products
      .filter((product) => {
        if (!showInactive && product.active === false) return false;
        if (categoryFilter !== "ALL" && product.category !== categoryFilter) return false;
        if (segmentFilter !== "ALL" && !(product.segments ?? []).includes(segmentFilter)) return false;
        if (query) {
          const q = query.toLowerCase();
          const matchesName = product.name.toLowerCase().includes(q);
          const matchesCode = product.code != null && String(product.code).includes(q);
          if (!matchesName && !matchesCode) return false;
        }
        return true;
      })
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [products, query, categoryFilter, segmentFilter, showInactive]);

  const segmentCounts = useMemo(() => {
    const counts = new Map<ClientType, number>();
    for (const product of products) {
      for (const segment of product.segments ?? []) {
        counts.set(segment, (counts.get(segment) ?? 0) + 1);
      }
    }
    return counts;
  }, [products]);

  const counts = useMemo(
    () => ({
      total: products.length,
      active: products.filter((product) => product.active).length,
      inactive: products.filter((product) => !product.active).length,
      processed: products.filter((product) => product.isProcessed).length,
    }),
    [products],
  );

  const openCreate = () => {
    setEditing({ ...emptyDraft });
    setDialogLoading(false);
    setDialogOpen(true);
  };

  const openEdit = async (id: string) => {
    setDialogOpen(true);
    setDialogLoading(true);
    setEditing(null);

    try {
      const detail = await fetchAdminProduct(id);
      setEditing({ ...detail });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel carregar o produto");
      setDialogOpen(false);
    } finally {
      setDialogLoading(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogLoading(false);
    setEditing(null);
  };

  const runExport = async (kind: "pdf" | "xlsx") => {
    setExporting(kind);
    try {
      const filters = {
        query,
        category: categoryFilter,
        includeInactive: showInactive,
      };
      if (kind === "pdf") await exportAdminProductsPdf(filters);
      else await exportAdminProductsXlsx(filters);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel exportar os produtos");
    } finally {
      setExporting(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAdminProduct(deleteTarget.id);
      toast.success("Produto excluído definitivamente");
      setDeleteTarget(null);
      await loadPageData();
    } catch (error) {
      // 409 traz a mensagem do backend (ex.: possui movimentações — sugere inativação).
      toast.error(error instanceof Error ? error.message : "Nao foi possivel excluir o produto");
    } finally {
      setDeleting(false);
    }
  };

  const openLabel = async (id: string) => {
    try {
      const detail = await fetchAdminProduct(id);
      setLabelProduct(detail);
      setLabelOpen(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel carregar o produto");
    }
  };

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container flex flex-col gap-4 py-8 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Produtos</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              {canManage
                ? "Cadastre, edite e ative produtos do catálogo."
                : "Consulte o catálogo e gere etiquetas dos produtos."}
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-xs">
              <Stat label="Total" value={counts.total} />
              <Stat label="Ativos" value={counts.active} />
              <Stat label="Inativos" value={counts.inactive} />
              <Stat label="Processados" value={counts.processed} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20"
                  disabled={exporting !== null}
                >
                  {exporting !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Exportar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => void runExport("pdf")}>
                  <FileText className="mr-2 h-4 w-4" />
                  Exportar em PDF
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void runExport("xlsx")}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Exportar em XLSX (Excel)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
            {canCreate && (
              <Button
                variant="hero"
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                onClick={openCreate}
              >
                <Plus className="h-4 w-4" /> Novo produto
              </Button>
            )}
          </div>
        </div>
      </section>

      <div className="container space-y-5 py-6">
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar produto por nome..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="h-14 bg-surface pl-12 text-base"
              />
            </div>
            <Button
              variant={showInactive ? "hero" : "outline"}
              onClick={() => setShowInactive((current) => !current)}
              className="h-14 shrink-0 px-5"
            >
              {showInactive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
              {showInactive ? "Ocultar inativos" : "Mostrar inativos"}
            </Button>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {categoryOptions.map((category) => (
              <Button
                key={category.id}
                variant="chip"
                size="sm"
                data-active={categoryFilter === category.id}
                onClick={() => setCategoryFilter(category.id)}
                className="shrink-0"
              >
                <span>{category.emoji}</span> {category.label}
              </Button>
            ))}
          </div>
        </div>

        {loadState === "loading" && (
          <div className="surface-card p-12 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
            Carregando produtos...
          </div>
        )}

        {loadState === "error" && (
          <div className="surface-card p-12 text-center text-sm text-destructive">
            {loadError ?? "Nao foi possivel carregar os produtos"}
          </div>
        )}

        {loadState !== "loading" && loadState !== "error" && (
          <>
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSegmentFilter("ALL")}
              className={cn(
                "rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
                segmentFilter === "ALL" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-surface hover:border-primary/40",
              )}
            >
              Todos os segmentos
            </button>
            {(Object.keys(CLIENT_TYPE_LABELS) as ClientType[]).map((segment) => (
              <button
                key={segment}
                type="button"
                onClick={() => setSegmentFilter(segment)}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
                  segmentFilter === segment ? "border-primary bg-primary text-primary-foreground" : "border-border bg-surface hover:border-primary/40",
                )}
              >
                Linha {CLIENT_TYPE_LABELS[segment]} <span className="opacity-70">({segmentCounts.get(segment) ?? 0})</span>
              </button>
            ))}
          </div>
          <div className="surface-card overflow-hidden">
            <div className="hidden grid-cols-[auto_1.5fr_1fr_1.2fr_1fr_auto] gap-4 border-b border-border bg-secondary/40 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground md:grid">
              <div className="w-12" />
              <div>Produto</div>
              <div>Categoria</div>
              <div>Unidades</div>
              <div>Tipo</div>
              <div className="text-right">Ações</div>
            </div>
            <ul className="divide-y divide-border">
              {filtered.map((product) => {
                const isInactive = !product.active;
                const units = product.availableUnits.length > 0 ? product.availableUnits : [product.defaultUnit];
                const category = categoryById.get(product.category);
                return (
                  <li
                    key={product.id}
                    className={cn(
                      "grid items-center gap-4 px-5 py-3 transition-colors hover:bg-secondary/30 md:grid-cols-[auto_1.5fr_1fr_1.2fr_1fr_auto]",
                      isInactive && "opacity-60",
                    )}
                  >
                    <ProductThumb
                      imageUrl={product.imageUrl}
                      name={product.name}
                      fallback={category?.emoji}
                      className="h-12 w-12 shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-display font-semibold">{product.name}</span>
                        {isInactive && (
                          <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            Inativo
                          </span>
                        )}
                      </div>
                      {product.code != null && (
                        <div className="text-xs text-muted-foreground tabular-nums">Cód. {product.code}</div>
                      )}
                    </div>
                    <div className="text-sm">{category?.label ?? product.category}</div>
                    <div className="flex flex-wrap gap-1 text-xs">
                      {units.map((unit) => (
                        <span
                          key={unit}
                          className={cn(
                            "rounded border px-1.5 py-0.5",
                            unit === product.defaultUnit
                              ? "border-primary/40 bg-primary/10 font-medium text-primary"
                              : "border-border bg-secondary/60 text-muted-foreground",
                          )}
                        >
                          {unit}
                        </span>
                      ))}
                    </div>
                    <div className="space-y-1 text-sm">
                      {product.isProcessed ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                          <Package className="h-3 w-3" /> Processado
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">In natura</span>
                      )}
                      {product.wastePercent != null && product.wastePercent > 0 ? (
                        <div className="inline-flex items-center gap-1 rounded bg-warning/10 px-1.5 py-0.5 text-[11px] text-warning">
                          <Scissors className="h-3 w-3" /> {formatPercent(product.wastePercent)}% merma
                        </div>
                      ) : null}
                    </div>
                    <div className="flex gap-2 md:justify-end">
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => void openEdit(product.id)}>
                          <Pencil className="h-4 w-4" /> Editar
                        </Button>
                      )}
                      {canGenerateLabels && (
                        <Button size="sm" variant="outline" onClick={() => void openLabel(product.id)}>
                          <Tag className="h-4 w-4" /> Etiqueta
                        </Button>
                      )}
                      {(isInactive ? canActivate : canDeactivate) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            void (async () => {
                              try {
                                await setAdminProductActive(product.id, isInactive);
                                await loadPageData();
                                toast.success(isInactive ? "Produto ativado" : "Produto desativado");
                              } catch (error) {
                                toast.error(error instanceof Error ? error.message : "Nao foi possivel atualizar o produto");
                              }
                            })()
                          }
                        >
                          {isInactive ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
                          {isInactive ? "Ativar" : "Desativar"}
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleteTarget(product)}
                        >
                          <Trash2 className="h-4 w-4" /> Apagar
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="px-5 py-12 text-center text-sm text-muted-foreground">Nenhum produto encontrado.</li>
              )}
            </ul>
          </div>
          </>
        )}
      </div>

      <LabelDialog open={labelOpen} product={labelProduct} onClose={() => setLabelOpen(false)} />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">ATENÇÃO!</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Esta ação excluirá definitivamente o produto
                {deleteTarget ? ` "${deleteTarget.name}"` : ""} e removerá todas as suas referências nas
                tabelas de preços.
              </span>
              <span className="block font-medium">Esta operação não poderá ser desfeita.</span>
              <span className="block">Deseja realmente continuar?</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => { event.preventDefault(); void confirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Apagar Produto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ProductDialog
        open={dialogOpen}
        loading={dialogLoading}
        draft={editing}
        categoryOptions={categoryOptions}
        units={catalogUnits}
        cutOptions={cutOptions}
        canManageUnits={canManageUnits}
        canViewCost={canViewCost}
        canEditCost={canEditCost}
        onCreateUnit={handleCreateUnit}
        onUpdateUnit={handleUpdateUnit}
        onFetchUnitUsage={fetchCatalogUnitUsage}
        onClose={closeDialog}
        onSave={async (data) => {
          try {
            if (editing?.id) {
              await updateAdminProduct(editing.id, data);
              toast.success("Produto atualizado");
            } else {
              await createAdminProduct(data);
              toast.success("Produto criado");
            }
            await loadPageData();
            closeDialog();
          } catch (error) {
            toast.error(error instanceof Error ? error.message : "Nao foi possivel salvar o produto");
          }
        }}
      />
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-primary-foreground/20 bg-primary-foreground/10 px-3 py-2 backdrop-blur-sm">
      <div className="font-display text-lg font-bold leading-none tabular-nums">{value}</div>
      <div className="mt-1 opacity-80">{label}</div>
    </div>
  );
}

